import React, { useEffect, useMemo } from 'react';
import t from 'react-translate';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import NvModal, { ModalType } from 'shared/components/nv-modal';
import { getCurrentCourse } from 'redux/selectors/course';
import { useSelector } from 'react-redux';
import { IHelpOptionsSettingsPayload } from 'redux/schemas/models/help-options';
import { useAppDispatch } from 'redux/store';
import { getHelpOptionsSettings, saveHelpOptionsSettings } from 'redux/actions/help-options';
import pusherService from 'shared/services/pusher-service';
import { getCurrentInstitution } from 'redux/selectors/institutions';
import AILearningAssistantSection from './ai-learning-assistant-section';
import HelpResourcesSection from './help-resources-section';
import { modalBodyStyles } from './styles';
import { AskNovoAiState } from '../ask-novoai/helpers';

type Props = {
  forwardShowModal: (func: () => void) => void;
};

interface IHelpOptionsFormValues {
  userCourseIds: number[];
  hasAiLearningAssistantEnabled: boolean;
  hasHelpResourcesEnabled: boolean;
  courseHelpTitle: string;
  courseHelpBody: string;
}

// Yup validation schema
const validationSchema = yup.object().shape({
  userCourseIds: yup.array().of(yup.number()),
  hasAiLearningAssistantEnabled: yup.boolean(),
  hasHelpResourcesEnabled: yup.boolean(),
  courseHelpTitle: yup.string().when('hasHelpResourcesEnabled', {
    is: true,
    then: yup.string().required().min(1),
    otherwise: yup.string(),
  }),
  courseHelpBody: yup.string().when('hasHelpResourcesEnabled', {
    is: true,
    then: yup.string().required().min(1),
    otherwise: yup.string(),
  }),
});


const QNA_PUSHER_EVENT = 'qna_embedding_completed';

interface PusherChannelPayload {
  has_qna_enabled: boolean
}

const HelpOptionsSettings = (props: Props) => {
  const { forwardShowModal } = props;
  const dispatch = useAppDispatch();
  const [show, setShow] = React.useState(false);
  const { hasAiLearningAssistant } = useSelector(getCurrentInstitution);
  const { catalogId, id } = useSelector(getCurrentCourse) as {
    catalogId: string;
    id: number;
  };

  const askNovoAi: AskNovoAiState = useSelector(
    (state) => state.app.askNovoAi,
  );
  const { helpOptions } = askNovoAi;

  const methods = useForm<IHelpOptionsFormValues>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
    defaultValues: {
      userCourseIds: [],
      hasAiLearningAssistantEnabled: false,
      hasHelpResourcesEnabled: false,
      courseHelpTitle: '',
      courseHelpBody: '',
    },
  });
  const watchUserCourseIds = methods.watch('userCourseIds');
  const watchHasAiLearningAssistantEnabled = methods.watch('hasAiLearningAssistantEnabled');

  React.useEffect(() => {
    if (helpOptions) {
      const {
        courseAdminUsers = [],
        hasAiLearningAssistantEnabled,
        hasHelpResourcesEnabled,
        hasAiEmbeddingInProgress,
      } = helpOptions;

      methods.reset({
        userCourseIds: courseAdminUsers?.reduce(
          (acc: number[], item) => {
            if (item.id && item.isQnaFacilitator) {
              return [...acc, item.id];
            }
            return acc;
          },
          [],
        ),
        hasAiLearningAssistantEnabled: hasAiLearningAssistant && (hasAiLearningAssistantEnabled || hasAiEmbeddingInProgress),
        hasHelpResourcesEnabled,
        courseHelpTitle: hasHelpResourcesEnabled ? helpOptions.courseHelp?.title : '',
        courseHelpBody: hasHelpResourcesEnabled ? helpOptions.courseHelp?.body : '',
      });
    }
  }, [hasAiLearningAssistant, helpOptions, methods]);


  // Listening for changes in the discussion key themes
  React.useEffect(() => {
    let pusherChannel = null;
    pusherChannel = pusherService.initializeCourseChannel(id);
    pusherChannel.bind(QNA_PUSHER_EVENT, (data: PusherChannelPayload) => {
      if (data.has_qna_enabled) {
        dispatch(getHelpOptionsSettings(catalogId));
      }
    });

    return () => {
      if (pusherChannel) pusherChannel.unbind(QNA_PUSHER_EVENT);
    };
  }, [catalogId, dispatch, id]);

  React.useEffect(() => {
    forwardShowModal(() => setShow(true));
  }, [forwardShowModal]);

  const handleModalClose = () => {
    setShow(false);
  };

  const onSubmit = (data: IHelpOptionsFormValues) => {
    const payload: IHelpOptionsSettingsPayload = {
      userCourseIds: data.userCourseIds,
      hasAiLearningAssistantEnabled: data.hasAiLearningAssistantEnabled,
      hasHelpResourcesEnabled: data.hasHelpResourcesEnabled,
      courseHelp: {
        title: data.courseHelpTitle,
        body: data.courseHelpBody,
      },
    };
    dispatch(saveHelpOptionsSettings({ catalogId, payload }));
    handleModalClose();
  };

  useEffect(() => {
    // Clean up the modal state when the modal is closed
    if (!show) {
      methods.reset();
    }
  }, [show, methods]);

  const disabledButton = useMemo(
    () => {
      // If user course IDs are selected, AI Learning Assistant must be enabled
      if (watchUserCourseIds.length > 0 && !watchHasAiLearningAssistantEnabled) return true;
      return !methods.formState.isDirty || !methods.formState.isValid;
    },
    [
      methods.formState.isDirty, methods.formState.isValid,
      watchUserCourseIds,
      watchHasAiLearningAssistantEnabled,
    ],
  );

  return (
    <NvModal
      show={show}
      height='auto'
      type={ModalType.FIXED}
      onClose={handleModalClose}
      header={t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.BADGE_TITLE()}
      body={(
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <div css={modalBodyStyles}>
              <AILearningAssistantSection
                users={helpOptions?.courseAdminUsers}
              />
              <HelpResourcesSection />
              <div className='custom-modal-footer'>
                <button
                  type='button'
                  className='btn btn-default'
                  onClick={handleModalClose}
                >
                  {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.CANCEL_BUTTON()}
                </button>
                <button
                  type='submit'
                  className='btn btn-primary'
                  disabled={disabledButton}
                >
                  {t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.CONFIRM_BUTTON()}
                </button>
              </div>
            </div>
          </form>
        </FormProvider>
      )}
    />
  );
};

export default HelpOptionsSettings;
